import { ServiceGraphosCloudMetricsRecord } from 'src/lib/graphqlTypes/types';

import {
  GraphOSMetricsRecord,
  NormalizedMetric,
  NormalizedMetricsResult,
} from '../types/types';

/**
 * Converts a byte value to either kilobytes (KB) or megabytes (MB), depending on its size.
 *
 * - If the byte value is greater than or equal to 1 MB (1048576 bytes), the result is in MB.
 * - If the byte value is smaller than 1 MB, it is converted to KB.
 */
const convertBytesToKBorMB = (bytes: number) => {
  const MB_THRESHOLD = 1048576;
  const KB_THRESHOLD = 1024;

  if (bytes >= MB_THRESHOLD) {
    return { value: bytes / MB_THRESHOLD, unit: 'MB' };
  } else {
    return { value: bytes / KB_THRESHOLD, unit: 'KB' };
  }
};

/**
 * Detects whether the majority of the data in the provided metrics is in kilobytes (KB)
 * or megabytes (MB).
 *
 * This function iterates over the provided metrics and keys to determine if the majority of values
 * should be labeled in KB or MB, by counting the number of KB and MB units in the data.
 *
 */
const detectKBorMBUnit = (
  normalizedData: MinLengthArray<1, GraphOSMetricsRecord>,
  keys: string[],
): string => {
  let kbCount = 0;
  let mbCount = 0;

  normalizedData.forEach((metric) => {
    keys.forEach((key) => {
      const value = metric.metrics[key];
      if (value !== null && value !== undefined) {
        const unit = convertBytesToKBorMB(value).unit;
        if (unit === 'MB') {
          mbCount += 1;
        } else {
          kbCount += 1;
        }
      }
    });
  });

  return mbCount > kbCount ? 'MB' : 'KB';
};

/**
 * Creates a placeholder metrics object in the event that no data is available.
 */
const handleEmptyMetrics = (
  keys: string[],
): MinLengthArray<1, GraphOSMetricsRecord> => {
  return [
    {
      metrics: keys.reduce((acc, key) => ({ ...acc, [key]: null }), {}),
      timestamp: new Date().toISOString(),
    },
  ];
};

function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

/**
 * If the value is a byte size and `isResponseSize` is true,
 * the value is converted from bytes to KB or MB. Otherwise, the value is returned as-is.
 */
export const normalizeMetric = (
  metric: ServiceGraphosCloudMetricsRecord['metrics'],
  key: keyof ServiceGraphosCloudMetricsRecord['metrics'] | string,
): number => {
  const rawValue =
    metric[key as keyof ServiceGraphosCloudMetricsRecord['metrics']];

  const value = isNumber(rawValue) ? rawValue : 0;

  if (key === 'responseSize' || key === 'responseSizeThrottled') {
    const { value: convertedValue } = convertBytesToKBorMB(value);
    return convertedValue;
  }

  return value;
};

/**
 * Normalizes all metric values for a single record. It converts values where necessary
 * (e.g., bytes to KB/MB for response sizes) and ensures that all relevant metrics
 * are included in the result.
 */
export const normalizeSingleMetric = (
  metric: Pick<ServiceGraphosCloudMetricsRecord, 'metrics' | 'timestamp'>,
  keys: string[],
): GraphOSMetricsRecord => {
  const normalizedMetrics: NormalizedMetric = {};

  keys.forEach((key) => {
    const normalizedValue = normalizeMetric(metric.metrics, key);
    normalizedMetrics[key] = normalizedValue || 0; // default to 0 if the value is undefined or null
  });

  return {
    timestamp: metric.timestamp,
    metrics: normalizedMetrics,
  };
};

/**
 * Main function to normalize all metrics data. This normalizes the data by converting
 * raw values (e.g., bytes) into more human-readable units (KB/MB), detects the appropriate unit
 * for display, and ensures that all metric records are consistent and complete.
 *
 */
export const normalizeMetrics = (
  metrics: Omit<ServiceGraphosCloudMetricsRecord, 'groupBy'>[],
  keys: string[],
): NormalizedMetricsResult => {
  const normalizedData: MinLengthArray<1, GraphOSMetricsRecord> =
    (metrics.map((metric) =>
      normalizeSingleMetric(metric, keys),
    ) as MinLengthArray<1, GraphOSMetricsRecord>) ||
    (handleEmptyMetrics(keys) as MinLengthArray<1, GraphOSMetricsRecord>);

  const detectedUnit = detectKBorMBUnit(normalizedData, keys);

  return {
    normalizedData,
    detectedUnit,
  };
};
